import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminClient, type AdminProjectRow } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminBreadcrumbs } from '@/components/shell/AdminBreadcrumbs';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { OrgUsagePanel } from '@/components/usage/OrgUsagePanel';
import { Button } from '@/components/ui/button';
import { formatCount, formatDate } from '@/lib/format';
import { orgBasePath, orgSectionPath } from '@/lib/orgNav';
import { humanizeKey } from '@/lib/runTelemetry';
import { useOrgScope } from '@/pages/org/OrgLayout';

export function ProjectDetailPage() {
  const { projectId = '' } = useParams();
  const { orgId } = useOrgScope();
  const [project, setProject] = useState<AdminProjectRow | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const row = await adminClient.getProject(orgId, projectId);
        if (cancelled) return;
        setProject(row);
        setError(undefined);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, projectId, reloadKey]);

  if (loading) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading project activity
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AdminPageLoadError
        message={error}
        onRetry={() => setReloadKey((key) => key + 1)}
        backTo={orgSectionPath(orgId, 'projects')}
        backLabel="Back to projects"
      />
    );
  }

  if (!project) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="space-y-3">
          <p className="text-sm text-destructive">Project not found</p>
          <Link
            to={orgSectionPath(orgId, 'projects')}
            className="text-sm text-primary hover:underline"
          >
            Back to projects
          </Link>
        </div>
      </div>
    );
  }

  const orgPath = orgBasePath(orgId);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminBreadcrumbs
        items={[
          { label: 'Projects', to: orgSectionPath(orgId, 'projects') },
          { label: project.displayName || project.title },
        ]}
      />
      <AdminPageHeader
        title={project.displayName || project.title}
        description="Project lifecycle: builds, costs and users since the project was created."
        actions={
          project.latestSessionId ? (
            <Button asChild size="sm" variant="outline">
              <Link to={`${orgPath}/sessions/${encodeURIComponent(project.latestSessionId)}`}>
                Latest session runs
              </Link>
            </Button>
          ) : null
        }
      />
      <dl className="mb-8 grid gap-x-6 gap-y-4 rounded-xl border border-border px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdminMetaItem label="Status">
          <StatusPill tone={project.status === 'active' ? 'success' : 'neutral'}>
            {humanizeKey(project.status)}
          </StatusPill>
        </AdminMetaItem>
        <AdminMetaItem label="Package">
          <span className="font-mono text-xs">{project.packageName || '—'}</span>
        </AdminMetaItem>
        <AdminMetaItem label="Environment">{project.environment || project.workspaceKind || '—'}</AdminMetaItem>
        <AdminMetaItem label="Published versions">{formatCount(project.publishedVersionCount)}</AdminMetaItem>
        <AdminMetaItem label="Created">{formatDate(project.createdAt)}</AdminMetaItem>
        <AdminMetaItem label="Updated">{formatDate(project.updatedAt)}</AdminMetaItem>
        <AdminMetaItem label="Latest session">
          {project.latestSessionId ? (
            <Link
              to={`${orgPath}/sessions/${encodeURIComponent(project.latestSessionId)}`}
              className="hover:underline"
            >
              {project.latestSessionTitle || project.latestSessionId}
            </Link>
          ) : (
            '—'
          )}
        </AdminMetaItem>
        <AdminMetaItem label="Published shell">
          {project.latestPublish?.shellUrl ? (
            <a
              href={project.latestPublish.shellUrl}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-primary hover:underline"
            >
              {project.latestPublish.shellUrl}
            </a>
          ) : (
            '—'
          )}
        </AdminMetaItem>
        <AdminMetaItem label="Project id" className="sm:col-span-2 lg:col-span-4">
          <span className="font-mono text-xs">{project.projectId}</span>
        </AdminMetaItem>
      </dl>
      <OrgUsagePanel
        orgId={orgId}
        projectId={projectId}
        lifecycleFrom={project.createdAt}
        emptyActivityLabel="No builds or integration events for this project across its lifecycle"
      />
    </div>
  );
}
